import {
  CaretRightOutlined,
  FastBackwardOutlined,
  FastForwardOutlined,
  PauseOutlined,
  SoundOutlined,
  MutedOutlined,
} from "@ant-design/icons";
import React, { useEffect, useRef, useState, forwardRef } from "react";
import styled from "styled-components";
import { Tooltip } from "antd";

const SPACING = {
  xs: "8px",
  sm: "16px",
};

const NewsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 768px) {
    height: 100%;
    min-height: calc(100dvh - 340px);
  }
`;

const NewsContent = styled.div<{ isVisible?: boolean }>`
  padding: ${(props) => props.isVisible ? "40px 40px 20px" : "20px"};
  height: ${(props) => props.isVisible ? "372px" : "initial"};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  border-radius: 16px;

  @media screen and (max-width: 768px) {
    padding: 0 20px;
    height: auto;
    flex: 1;
    min-height: 0;
  }
`;

const NewsCaption = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: white;
  text-align: left;
  margin-bottom: 20px;
  line-height: 1.4;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  transition: transform 0.1s ease-linear;
  padding-right: 10px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: transparent;
  }

  @media screen and (max-width: 768px) {
    font-size: 18px;
    max-height: calc(18px * 1.4 * 5);
  }
`;

const NewsTitle = styled.div`
  font-size: 20px;
  font-weight: 500;
  color: white;
  text-align: center;
  opacity: 0.9;

  @media screen and (max-width: 768px) {
    font-size: 16px;
  }
`;

const MobileNewsTitle = styled.div`
  font-size: 20px;
  font-weight: 500;
  color: white;
  text-align: center;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
`;

const VideoControls = styled.div`
  //position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 20px 40px;

  @media screen and (max-width: 768px) {
    padding: 20px;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  margin-bottom: 16px;
  position: relative;
  cursor: pointer;
`;

const MobileProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  margin-top: 8px;
  position: relative;
  cursor: pointer;
`;

const ProgressFill = styled.div`
  width: 0%;
  height: 100%;
  background: #f97316;
  border-radius: 2px;
  transition: none;
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const TimeText = styled.span`
  color: white;
  font-size: 14px;
  font-weight: 500;
`;

const ControlButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  position: relative;
`;

const ControlButton = styled.button<{ isActive?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${(props) =>
    props.isActive ? "#f97316" : "rgba(255, 255, 255, 0.2)"};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 16px;

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.isActive ? "#ea580c" : "rgba(255, 255, 255, 0.3)"};
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PlayPauseButton = styled.button<{ isPlaying: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  background: #f97316;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 20px;

  &:hover {
    background: #ea580c;
    transform: scale(1.05);
  }
`;

const MuteButtonContainer = styled.div`
  position: absolute;
  right: 0;

  @media screen and (min-width: 769px) {
    display: none;
  }
`;

const MobileNewsContainer = styled.div`
  width: calc(100% - 76px);
  display: flex;
  gap: ${SPACING.sm};
  align-items: center;
`;

const MobileNewsContent = styled.div`
  width: calc(100% - 56px);
`;

const MobileNewsControls = styled.div`
  display: flex;
  gap: ${SPACING.xs};
`;

// Helper function to format time
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

interface NewsDisplayProps {
  currentNewsTitle: string;
  newsDuration: number;
  fullContent?: string;
  isPlayingNews: boolean;
  isNewsPaused: boolean;
  isMuted: boolean;
  getCurrentAudioTime: () => number;
  onPlayPause: () => void;
  onToggleMute: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  isMobileMapView?: boolean;
  isPlayingOnMap?: boolean;
}

const News = forwardRef<HTMLDivElement, NewsDisplayProps>(({
  currentNewsTitle,
  newsDuration,
  fullContent,
  isPlayingNews,
  isNewsPaused,
  isMuted,
  getCurrentAudioTime,
  onPlayPause,
  onToggleMute,
  onNext,
  onPrev,
  isMobileMapView = false,
  isPlayingOnMap = false,
}, captionRef) => {
  const progressFillRef = useRef<HTMLDivElement>(null);
  const mobileProgressFillRef = useRef<HTMLDivElement>(null);
  const timeTextRef = useRef<HTMLSpanElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastSyncTimeRef = useRef<number>(0);
  const [showTooltip, setShowTooltip] = useState(true);

  useEffect(() => {
    if (isMobileMapView) return;
    
    if (!progressFillRef.current || !timeTextRef.current || !newsDuration || newsDuration === 0) {
      return;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    const syncWithAudio = () => {
      if (!progressFillRef.current || !timeTextRef.current) return;
      
      const currentTime = getCurrentAudioTime();
      const progress = (currentTime / newsDuration) * 100;
      progressFillRef.current.style.width = `${progress}%`;
      timeTextRef.current.textContent = formatTime(currentTime);
      lastSyncTimeRef.current = currentTime;
    };

    syncWithAudio();

    if (isPlayingNews && !isNewsPaused) {
      let lastSyncCheckTime = performance.now();

      const updateProgress = () => {
        if (!progressFillRef.current || !timeTextRef.current) return;

        const now = performance.now();

        if (now - lastSyncCheckTime > 100) {
          const currentTime = getCurrentAudioTime();
          const progress = (currentTime / newsDuration) * 100;
          progressFillRef.current.style.width = `${progress}%`;
          timeTextRef.current.textContent = formatTime(currentTime);
          lastSyncTimeRef.current = currentTime;
          lastSyncCheckTime = now;
        } else {
          const elapsed = (now - lastSyncCheckTime) / 1000;
          const currentTime = Math.min(lastSyncTimeRef.current + elapsed, newsDuration);
          const progress = (currentTime / newsDuration) * 100;
          progressFillRef.current.style.width = `${progress}%`;
          timeTextRef.current.textContent = formatTime(currentTime);
        }

        if (isPlayingNews && !isNewsPaused) {
          animationFrameRef.current = requestAnimationFrame(updateProgress);
        } else {
          animationFrameRef.current = null;
        }
      };

      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isPlayingNews, isNewsPaused, newsDuration, getCurrentAudioTime, isMobileMapView]);

  useEffect(() => {
    if (!isMobileMapView || !newsDuration || newsDuration === 0) {
      return;
    }

    let animationFrameRef: number | null = null;
    let lastSyncTime = 0;
    let lastSyncCheckTime = performance.now();

    const updateProgress = () => {
      if (!mobileProgressFillRef.current) {
        if (animationFrameRef) {
          cancelAnimationFrame(animationFrameRef);
          animationFrameRef = null;
        }
        return;
      }

      const now = performance.now();

      if (now - lastSyncCheckTime > 100) {
        const currentTime = getCurrentAudioTime();
        const progress = Math.min((currentTime / newsDuration) * 100, 100);
        if (mobileProgressFillRef.current) {
          mobileProgressFillRef.current.style.width = `${progress}%`;
        }
        lastSyncTime = currentTime;
        lastSyncCheckTime = now;
      } else {
        const elapsed = (now - lastSyncCheckTime) / 1000;
        const currentTime = Math.min(lastSyncTime + elapsed, newsDuration);
        const progress = Math.min((currentTime / newsDuration) * 100, 100);
        if (mobileProgressFillRef.current) {
          mobileProgressFillRef.current.style.width = `${progress}%`;
        }
      }

      if (
        isPlayingNews &&
        !isNewsPaused &&
        mobileProgressFillRef.current
      ) {
        animationFrameRef = requestAnimationFrame(updateProgress);
      } else {
        animationFrameRef = null;
      }
    };

    const startAnimation = () => {
      if (!mobileProgressFillRef.current) {
        setTimeout(startAnimation, 50);
        return;
      }

      const currentTime = getCurrentAudioTime();
      const progress = Math.min((currentTime / newsDuration) * 100, 100);
      if (mobileProgressFillRef.current) {
        mobileProgressFillRef.current.style.width = `${progress}%`;
      }
      lastSyncTime = currentTime;

      if (isPlayingNews && !isNewsPaused) {
        animationFrameRef = requestAnimationFrame(updateProgress);
      }
    };

    const timeoutId = setTimeout(startAnimation, 0);

    return () => {
      clearTimeout(timeoutId);
      if (animationFrameRef) {
        cancelAnimationFrame(animationFrameRef);
        animationFrameRef = null;
      }
    };
  }, [isMobileMapView, isPlayingNews, isNewsPaused, newsDuration, getCurrentAudioTime]);

  const handleToggleMute = () => {
    setShowTooltip(false);
    onToggleMute();
  };

  const handleMobilePlayPause = () => {
    const wasShowingTooltip = showTooltip;
    handleToggleMute();
    if (wasShowingTooltip && isMuted) return;
    onPlayPause();
  };

  if (!currentNewsTitle) {
    return null;
  }

  if (isMobileMapView) {
    return (
      <MobileNewsContainer>
        <MobileNewsContent>
          <MobileNewsTitle>{currentNewsTitle || ""}</MobileNewsTitle>
          <MobileProgressBar>
            <ProgressFill ref={mobileProgressFillRef} />
          </MobileProgressBar>
        </MobileNewsContent>
        <MobileNewsControls>
          <Tooltip
            title={"Click để nghe"}
            open={showTooltip}
            arrow={true}
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement || document.body
            }
            builtinPlacements={{
              top: {
                points: ["bc", "tc"],
                offset: [-24, -24],
              },
            }}
            overlayInnerStyle={{
              boxShadow: "inset 0 0 4px #cccccc80",
            }}
          >
            <ControlButton
              isActive={true}
              onClick={handleMobilePlayPause}
            >
              {showTooltip || isNewsPaused ? (
                <CaretRightOutlined />
              ) : (
                <PauseOutlined />
              )}
            </ControlButton>
          </Tooltip>
        </MobileNewsControls>
      </MobileNewsContainer>
    );
  }

  return (
    <NewsContainer>
      <NewsContent isVisible={!!(fullContent && fullContent.length > 0 && !isPlayingOnMap)}>
        <NewsCaption ref={captionRef}></NewsCaption>

        <NewsTitle>
          {currentNewsTitle ||
            ""}
        </NewsTitle>
      </NewsContent>

      <VideoControls>
        <ProgressBar>
          <ProgressFill ref={progressFillRef} />
        </ProgressBar>

        <TimeDisplay>
          <TimeText ref={timeTextRef}>{formatTime(getCurrentAudioTime())}</TimeText>
          <TimeText>{formatTime(newsDuration)}</TimeText>
        </TimeDisplay>

        <ControlButtons>
          <ControlButton onClick={onPrev} disabled={!onPrev}>
            <FastBackwardOutlined />
          </ControlButton>

          <PlayPauseButton
            isPlaying={isPlayingNews && !isNewsPaused}
            onClick={onPlayPause}
          >
            {isNewsPaused ? <CaretRightOutlined /> : <PauseOutlined />}
          </PlayPauseButton>

          <ControlButton onClick={onNext} disabled={!onNext}>
            <FastForwardOutlined />
          </ControlButton>

          <MuteButtonContainer>
            <Tooltip 
              title={"Click để nghe"}
              placement={'bottom'}
              open={showTooltip}
              arrow={true}
              overlayInnerStyle={{ boxShadow: 'inset 0 0 4px #cccccc80' }}
            >
              <ControlButton isActive={!isMuted} onClick={handleToggleMute}>
                {isMuted ? <MutedOutlined /> : <SoundOutlined />}
              </ControlButton>
            </Tooltip>
          </MuteButtonContainer>
        </ControlButtons>
      </VideoControls>
    </NewsContainer>
  );
});

News.displayName = 'News';

export { News };
